import { useEffect, useMemo, useRef, useState } from 'react'

import { calcReportParamsSection, resolveReportParams } from '@aytm/app/stats/page/question-views/emd/lib/report-params'

// Owns the emotional-dial block state (view mode, line mode, persisted video position), seeded
// from the emd<key> report-param section and re-seeded whenever that section changes outside the
// block.
//
// The page view never reads the section itself: it receives an api on mount and pulls the
// payloads it needs (report-param section, export data) when a change is announced through
// onStateChange.
export default function useEmotionalDialState({ params, onReady, onStateChange }) {
  const [state, setState] = useState(() => resolveReportParams({ section: params?.emd }))

  const snapshot = useRef()
  snapshot.current = state

  const api = useMemo(
    () => ({
      getReportParamsSection: () => calcReportParamsSection(snapshot.current),
      getExportData: () => ({ ...snapshot.current })
    }),
    []
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  const sectionKey = JSON.stringify(params?.emd ?? null)
  const lastSectionKey = useRef(sectionKey)
  useEffect(() => {
    if (lastSectionKey.current === sectionKey) return
    lastSectionKey.current = sectionKey

    setState(resolveReportParams({ section: JSON.parse(sectionKey) ?? undefined }))
  }, [sectionKey])

  // The reason tells the view what the change means: 'seed' is the resolved starting state,
  // 'state' a change the researcher made.
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  const lastState = useRef(null)
  useEffect(() => {
    const reason = null === lastState.current ? 'seed' : 'state'
    lastState.current = state

    onStateChangeRef.current?.(reason)
  }, [state])

  const onModelChange = useMemo(
    () =>
      (change = {}) =>
        setState(current =>
          Object.entries(change).every(([key, value]) => current[key] === value) ? current : { ...current, ...change }
        ),
    []
  )

  return { ...state, onModelChange }
}
